import {JOBFEED_SLICE_NAME} from '@jobseeker/common-core/constants';
import {axiosInterceptorInstance} from '@jobseeker/common-core/helpers';
import {createAsyncThunk} from '@reduxjs/toolkit';
import {jobfeedService} from '@jobseeker/common-core/services';
import {AxiosError} from 'axios';
import {
  AppliedJobRequest,
  CategoryJobFeedRequest,
  JobFeedRequest,
  LocationJobFeedRequest,
  RecruiterJobFeedRequest,
  SavedJobRequest,
  SaveJobRequest,
  SimilarJobFeedRequest,
  TagJobFeedRequest,
  UnFollowJobRequest,
  UnFollowRecruiterRequest,
} from './requestInterfaces';
const axios = axiosInterceptorInstance(
  `${process.env.NEXT_PUBLIC_API_GATEWAY_GLADIATOR}/${process.env.NEXT_PUBLIC_PREFIX_JOB_SERVICE}`
);
export const fetchUserJobFeed = createAsyncThunk(
  `${JOBFEED_SLICE_NAME}/fetchUserJobFeed`,
  async ({userId, ...params}: JobFeedRequest, ThunkService) => {
    try {
      const modifiedParams = {...params};
      const response = await axios.get(`/jobfeed`, {
        params: modifiedParams,
        headers: {version: 2},
      });
      return response.data;
    } catch (error) {
      const err = error as AxiosError;
      console.error(err);
      throw ThunkService.rejectWithValue(err.response?.data);
    }
  }
);

export const unFollowJob = createAsyncThunk(
  `${JOBFEED_SLICE_NAME}/unFollowJob`,
  async ({userId, jobId, ...params}: UnFollowJobRequest, ThunkService) => {
    const url = `/jobfeed/${userId}/unfollowjob/${jobId}/${
      params.undo ? 'undo' : ''
    }`;
    try {
      const response = await axios.put(url);
      return response.data;
    } catch (error) {
      const err = error as AxiosError;
      throw ThunkService.rejectWithValue(err.response?.data);
    }
  }
);

export const unFollowRecruiter = createAsyncThunk(
  `${JOBFEED_SLICE_NAME}/unFollowRecruiter`,
  async (
    {userId, recruiterId, ...params}: UnFollowRecruiterRequest,
    ThunkService
  ) => {
    const url = `/jobfeed/${userId}/unfollowrecruiter/${recruiterId}/${
      params.undo ? 'undo' : ''
    }`;
    try {
      const response = await axios.put(url);
      return response.data;
    } catch (error) {
      const err = error as AxiosError;
      throw ThunkService.rejectWithValue(err.response?.data);
    }
  }
);

export const fetchCategoryJobFeed = createAsyncThunk(
  `${JOBFEED_SLICE_NAME}/fetchCategoryJobFeed`,
  async ({userId, catId, ...params}: CategoryJobFeedRequest, ThunkService) => {
    try {
      const jobList = await jobfeedService.getCategoryJobs({
        userId,
        catId,
        ...params,
      });
      return jobList;
    } catch (error) {
      const err = error as AxiosError;
      throw ThunkService.rejectWithValue(err.response?.data);
    }
  }
);

export const fetchRecuiterJobFeed = createAsyncThunk(
  `${JOBFEED_SLICE_NAME}/fetchRecuiterJobFeed`,
  async (
    {userId, recruiterId, ...params}: RecruiterJobFeedRequest,
    ThunkService
  ) => {
    try {
      const jobList = await jobfeedService.getRecuiterJobFeed({
        userId,
        recruiterId,
        ...params,
      });
      return jobList;
    } catch (error) {
      const err = error as AxiosError;
      throw ThunkService.rejectWithValue(err.response?.data);
    }
  }
);

export const fetchTagJobFeed = createAsyncThunk(
  `${JOBFEED_SLICE_NAME}/fetchTagJobFeed`,

  async ({userId, ...params}: TagJobFeedRequest, ThunkService) => {
    try {
      const jobList = await jobfeedService.getTagJobs({
        userId,
        ...params,
      });
      return jobList;
    } catch (error) {
      const err = error as AxiosError;
      throw ThunkService.rejectWithValue(err.response?.data);
    }
  }
);

export const fetchLocationBasedJobFeed = createAsyncThunk(
  `${JOBFEED_SLICE_NAME}/fetchLocationBasedJobFeed`,

  async (params: LocationJobFeedRequest, ThunkService) => {
    try {
      const jobList = await jobfeedService.getLocationSpecificJobs({
        ...params,
      });
      return jobList;
    } catch (error) {
      const err = error as AxiosError;
      throw ThunkService.rejectWithValue(err.response?.data);
    }
  }
);

export const saveJob = createAsyncThunk(
  `${JOBFEED_SLICE_NAME}/saveJob`,
  async ({userId, jobId, ...params}: SaveJobRequest, ThunkService) => {
    const url = `/${params.unsave ? 'unsave' : 'save'}/?jobcode=${jobId}`;
    try {
      const response = await axios.post(url, {});
      ThunkService.dispatch({
        payload: params.unsave ? 'unsave' : 'save',
        type: 'User/adjust_saved_jobs_count',
      });
      return response.data;
    } catch (error) {
      const err = error as AxiosError;
      // console.error(err);
      throw ThunkService.rejectWithValue(err.response?.data);
    }
  }
);

export const fetchSimilarJobs = createAsyncThunk(
  `${JOBFEED_SLICE_NAME}/fetchSimilarJobs`,
  async ({userId, tags, ...params}: SimilarJobFeedRequest, ThunkService) => {
    try {
      const jobList = await jobfeedService.getSimilarJobs({
        userId,
        tags,
        ...params,
      });
      return jobList;
    } catch (error) {
      const err = error as AxiosError;
      throw ThunkService.rejectWithValue(err.response?.data);
    }
  }
);

export const findSavedJobs = createAsyncThunk(
  `${JOBFEED_SLICE_NAME}/findSavedJobs`,
  async ({page}: SavedJobRequest, ThunkService) => {
    try {
      const jobList = await jobfeedService.getSavedJobs(page);
      return jobList;
    } catch (error) {
      const err = error as AxiosError;
      throw ThunkService.rejectWithValue(err.response?.data);
    }
  }
);

export const findCompanySimilarJobs = createAsyncThunk(
  `${JOBFEED_SLICE_NAME}/findCompanySimilarJobs`,
  async ({page}: SavedJobRequest, ThunkService) => {
    try {
      const jobList = await jobfeedService.getCompanySimilarJobs(page);
      return jobList;
    } catch (error) {
      const err = error as AxiosError;
      throw ThunkService.rejectWithValue(err.response?.data);
    }
  }
);

export const getSavedJobCount = createAsyncThunk(
  `${JOBFEED_SLICE_NAME}/savedJobCount`,
  async (_, ThunkService) => {
    try {
      const response = await jobfeedService.getSavedJobCount();
      return response.data;
    } catch (error) {
      const err = error as AxiosError;
      throw ThunkService.rejectWithValue(err.response?.data);
    }
  }
);

export const findAppliedJobs = createAsyncThunk(
  `${JOBFEED_SLICE_NAME}/findAppliedJobs`,
  async ({page, status}: AppliedJobRequest, ThunkService) => {
    try {
      const jobList = await jobfeedService.getAppliedJobs(page, status);
      return jobList;
    } catch (error) {
      const err = error as AxiosError;
      throw ThunkService.rejectWithValue(err.response?.data);
    }
  }
);

export const searchJobsbyQuery = createAsyncThunk(
  `${JOBFEED_SLICE_NAME}/searchJobsbyQuery`,
  async ({userId, ...params}: JobFeedRequest, ThunkService) => {
    try {
      const modifiedParams = {...params};
      if (process.env.NEXT_PUBLIC_APP_NAME === 'hirist') {
        modifiedParams.size = 20;
      }
      const response = await axios.get(`/search`, {
        params: modifiedParams,
        headers: {version: 2},
      });
      return response.data;
    } catch (error) {
      const err = error as AxiosError;
      throw ThunkService.rejectWithValue(err.response?.data);
    }
  }
);

export const alumniPageJobs = createAsyncThunk(
  `${JOBFEED_SLICE_NAME}/alumniPageJobs`,
  async ({userId, ...params}: JobFeedRequest, ThunkService) => {
    try {
      const pageSize = 20;
      const response = await axios.get(`/alumni-jobs`, {
        params,
      });
      // Make the response format consistent with the other jobfeed APIs
      const data = response.data.data;
      response.data.data = data.jobs;
      // Calculate hasMore if not returned by API
      if (data.hasMore === undefined) {
        response.data.hasMore = data.jobs?.length >= pageSize;
      }
      return response.data;
    } catch (error) {
      const err = error as AxiosError;
      throw ThunkService.rejectWithValue(err.response?.data);
    }
  }
);

export const fetchRecommendedJobs = createAsyncThunk(
  `${JOBFEED_SLICE_NAME}/fetchRecommendedJobs`,
  async ({userId, ...params}: JobFeedRequest, ThunkService) => {
    try {
      const modifiedParams = {...params};
      if (process.env.NEXT_PUBLIC_APP_NAME === 'hirist') {
        modifiedParams.size = 20;
      }
      const response = await axios.get(`/jobfeed`, {
        params: modifiedParams,
      });
      return response.data;
    } catch (error) {
      const err = error as AxiosError;
      throw ThunkService.rejectWithValue(err.response?.data);
    }
  }
);

export const fetchRecruiterJobs = createAsyncThunk(
  `${JOBFEED_SLICE_NAME}/fetchRecruiterJobs`,
  async (
    {userId, recruiterId, ...params}: RecruiterJobFeedRequest,
    ThunkService
  ) => {
    try {
      const modifiedParams = {...params};
      if (process.env.NEXT_PUBLIC_APP_NAME === 'hirist') {
        modifiedParams.size = 20;
      }
      const response = await axios.get(
        `/jobfeed/${userId}/recruiter/${recruiterId}`,
        {
          params: modifiedParams,
        }
      );
      return response.data.data;
    } catch (error) {
      const err = error as AxiosError;
      throw ThunkService.rejectWithValue(err.response?.data);
    }
  }
);

export const fetchIndustryData = createAsyncThunk(
  `${JOBFEED_SLICE_NAME}/data/industry`,
  async (_, ThunkService) => {
    console.log('fetchIndustryData, ThunkService');
    try {
      const response = await axios.get(`/data/industry`);
      return response.data.data;
    } catch (error) {
      const err = error as AxiosError;
      throw ThunkService.rejectWithValue(err.response?.data);
    }
  }
);

export const findRecommendedJobs = createAsyncThunk(
  `${JOBFEED_SLICE_NAME}/findRecommendedJobs`,
  async ({page}: SavedJobRequest, ThunkService) => {
    try {
      const jobList = await jobfeedService.getRecommendedJobs(page);
      return jobList.data;
    } catch (error) {
      const err = error as AxiosError;
      throw ThunkService.rejectWithValue(err.response?.data);
    }
  }
);

export const findJobsForHer = createAsyncThunk(
  `${JOBFEED_SLICE_NAME}/findJobsForHer`,
  async (
    {
      femaleFilter,
      minExp,
      maxExp,
      joblocationIds,
      categoryIds,
      start,
      end,
      functionalArea,
      antifunctionalarea,
      centralisedCompanyIds,
      antiCentralisedCompanyIds,
    }: any,
    ThunkService
  ) => {
    try {
      const jobList = await jobfeedService.getFemaleJobs(
        femaleFilter,
        minExp,
        maxExp,
        categoryIds,
        joblocationIds,
        start,
        end,
        functionalArea,
        antifunctionalarea,
        centralisedCompanyIds,
        antiCentralisedCompanyIds
      );
      return jobList.data;
    } catch (error) {
      const err = error as AxiosError;
      throw ThunkService.rejectWithValue(err.response?.data);
    }
  }
);
